import type { CouponDetail, CouponDuration } from '@/features/auth/types'

export const ADVICEPREP_ENTRY_FEE = 125
export const ADVICEPREP_ENTRY_CONNECTIONS = 5
export const ADVICEPREP_DEFAULT_CONNECTIONS = 25
export const ADVICEPREP_MAX_SLIDER_CONNECTIONS = 200

/** Billable connections included free for the length of a trial. */
export const ADVICEPREP_TRIAL_CONNECTIONS = 2

/**
 * AdvicePrep meters Xero organisations only, so a connection of any other type
 * can never be on the meter and a missing billing flag on one tells us nothing
 * we did not already know.
 */
export const ADVICEPREP_BILLABLE_PLATFORM = 'Xero'

export type AdvicePrepBand = {
  from: number
  to: number
  rate: number
  label: string
}

export const ADVICEPREP_BANDS: AdvicePrepBand[] = [
  { from: 6, to: 10, rate: 22, label: '6 – 10' },
  { from: 11, to: 25, rate: 21, label: '11 – 25' },
  { from: 26, to: 50, rate: 17, label: '26 – 50' },
  { from: 51, to: 100, rate: 12, label: '51 – 100' },
  { from: 101, to: Number.POSITIVE_INFINITY, rate: 10, label: '101+' },
]

export function normalizeConnections(value: unknown): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 1) return 1
  return Math.floor(parsed)
}

export function advicePrepBandFor(connections: number): AdvicePrepBand | undefined {
  const total = normalizeConnections(connections)
  return ADVICEPREP_BANDS.find((band) => total >= band.from && total <= band.to)
}

/** Stripe meters by the day at a year's rate spread over 365 days, not a month's over 30. */
export function advicePrepDailyRate(monthlyRate: number): number {
  return (monthlyRate * 12) / 365
}

export type AdvicePrepDailyCharge = {
  band: AdvicePrepBand
  extra: number
  amount: number
}

/** One day at a count, or null inside the entry allowance. Stripe rounds per invoice line, not per day. */
export function advicePrepDailyCharge(connections: number): AdvicePrepDailyCharge | null {
  const total = normalizeConnections(connections)
  const band = advicePrepBandFor(total)
  if (!band) return null
  const extra = total - ADVICEPREP_ENTRY_CONNECTIONS
  return { band, extra, amount: Math.round(extra * advicePrepDailyRate(band.rate) * 100) / 100 }
}

/**
 * A month at a steady count. Every connection past the entry allowance is billed
 * at the rate of the band the total falls in, so crossing into a band lowers the price.
 */
export function calculateAdvicePrepPrice(connections: number): number {
  const total = normalizeConnections(connections)
  const band = advicePrepBandFor(total)
  if (!band) return ADVICEPREP_ENTRY_FEE
  return ADVICEPREP_ENTRY_FEE + (total - ADVICEPREP_ENTRY_CONNECTIONS) * band.rate
}

/**
 * The committed monthly charge, and the only amount due at subscribe time.
 * Connections past the entry allowance are usage-based and billed in arrears,
 * so the connections input never decides what the customer pays today.
 */
export function advicePrepCommittedMonthly(): number {
  return ADVICEPREP_ENTRY_FEE
}

/** Estimated usage charge on top of the flat fee at a given connection count. */
export function estimateAdvicePrepUsage(connections: number): number {
  return calculateAdvicePrepPrice(connections) - ADVICEPREP_ENTRY_FEE
}

export function formatAdvicePrepCurrency(amount: number, fractionDigits = 0): string {
  return `$${amount.toLocaleString('en-AU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

export type AdvicePrepAppliedCoupon = {
  code: string
  discount: number
  duration: CouponDuration | null
  durationInMonths: number | null
}

export type AdvicePrepCouponCopy = {
  applied: string
  renewal: string | null
}

const COUPON_DURATIONS: readonly CouponDuration[] = ['forever', 'once', 'repeating']

/** The response is parsed JSON, so the declared types are a claim, not a check. */
function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function toCouponDuration(value: unknown): CouponDuration | null {
  return COUPON_DURATIONS.find((duration) => duration === value) ?? null
}

/**
 * Discount against the committed monthly fee. Stripe gives percent_off as a whole
 * percentage and amount_off in the smallest currency unit. The AdvicePrep product
 * is AUD-only, so amount_off needs no currency conversion.
 */
export function advicePrepCouponDiscount(coupon: CouponDetail | null | undefined): number {
  if (!coupon) return 0

  const committed = advicePrepCommittedMonthly()
  const { percent_off: percentOff, amount_off: amountOff } = coupon

  if (isPositiveNumber(percentOff)) return Math.min(committed, Math.round(committed * percentOff) / 100)
  if (isPositiveNumber(amountOff)) return Math.min(committed, amountOff / 100)

  return 0
}

export function advicePrepAppliedCoupon(code: string, coupon: CouponDetail | null | undefined): AdvicePrepAppliedCoupon {
  const months = coupon?.duration_in_months
  return {
    code,
    discount: advicePrepCouponDiscount(coupon),
    duration: toCouponDuration(coupon?.duration),
    durationInMonths: isPositiveNumber(months) ? months : null,
  }
}

export function advicePrepCouponDiscountApplied(
  couponValid: boolean,
  coupon: AdvicePrepAppliedCoupon | null,
): number {
  return couponValid ? coupon?.discount ?? 0 : 0
}

/** The single figure the order summary and the pay button both promise. */
export function advicePrepDueToday(couponValid: boolean, coupon: AdvicePrepAppliedCoupon | null): number {
  return Math.max(0, advicePrepCommittedMonthly() - advicePrepCouponDiscountApplied(couponValid, coupon))
}

/**
 * Both sentences a discounted coupon produces, so the pricing step and the order
 * summary cannot describe the same coupon differently. Every figure names the flat
 * fee — usage is billed on top and is not discounted by the committed price.
 */
export function advicePrepCouponCopy(coupon: AdvicePrepAppliedCoupon | null): AdvicePrepCouponCopy | null {
  if (!coupon || coupon.discount <= 0) return null

  const committed = advicePrepCommittedMonthly()
  const off = formatAdvicePrepCurrency(coupon.discount, 2)
  const discounted = formatAdvicePrepCurrency(committed - coupon.discount, 2)
  const full = formatAdvicePrepCurrency(committed, 2)

  if (coupon.duration === 'forever') {
    return {
      applied: `${off} comes off the flat fee every month.`,
      renewal: `Renews at ${discounted} a month for the flat fee.`,
    }
  }

  const months = coupon.durationInMonths ?? 0
  if (coupon.duration === 'repeating' && months > 1) {
    const remaining = months - 1
    return {
      applied: `${off} comes off the flat fee for your first ${months} months.`,
      renewal: `The next ${remaining} ${remaining > 1 ? 'months are' : 'month is'} ${discounted} for the flat fee, then ${full}.`,
    }
  }

  // A repeating coupon with no month count could last any length, so the only
  // honest claim is the one invoice we can see. One month is a once coupon.
  const lastsOneInvoice = coupon.duration === 'once' || (coupon.duration === 'repeating' && months === 1)
  return {
    applied: `${off} comes off your first payment.`,
    renewal: lastsOneInvoice ? `Renews at ${full} a month for the flat fee.` : null,
  }
}

/**
 * A coupon that wipes out the whole flat fee is a free trial: nothing is due
 * today and billing starts when it runs out. A `forever` coupon zeroes every
 * invoice, so it is a comped account rather than a trial and has no end date
 * to promise. Returns the trial length in months, or null when the coupon is
 * an ordinary discount.
 */
export function advicePrepTrialMonths(coupon: AdvicePrepAppliedCoupon | null): number | null {
  if (!coupon) return null
  if (coupon.discount < advicePrepCommittedMonthly()) return null

  // `once` is one free invoice, so one month. A repeating coupon with no month
  // count could run for any length, and the screens that read this name a firm
  // end date — so an unknown length is not a trial we can describe, the same
  // caution advicePrepCouponCopy takes. `forever` is a comped account with no
  // end date at all, and an unrecognised duration tells us nothing.
  if (coupon.duration === 'once') return 1
  if (coupon.duration === 'repeating') return coupon.durationInMonths

  return null
}

/**
 * Clamped to the last day of the target month. `setMonth` alone overflows —
 * 31 January plus a month lands on 3 March — and this date is shown to the
 * customer as the day billing starts, so it cannot drift.
 *
 * Still an estimate from the browser clock, not the trial_end Stripe will
 * return: only good enough for the pre-subscribe promise.
 */
export function advicePrepTrialEnd(months: number, from: Date = new Date()): Date {
  const day = from.getDate()
  const end = new Date(from.getTime())

  end.setDate(1)
  end.setMonth(end.getMonth() + months)

  const lastDayOfTargetMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
  end.setDate(Math.min(day, lastDayOfTargetMonth))

  return end
}

const MS_PER_DAY = 86_400_000

/**
 * Whole days until the trial ends, counted from midnight to midnight so the
 * figure does not change mid-afternoon. Zero on the last day; null when the
 * date is missing or already past, which the caller shows differently from
 * "0 days" — an elapsed trial is not a trial with no time left on it.
 */
export function advicePrepTrialDaysLeft(trialEnd: string | null, from: Date = new Date()): number | null {
  if (!trialEnd) return null

  const end = new Date(trialEnd)
  if (Number.isNaN(end.getTime())) return null

  const endMidnight = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())
  const fromMidnight = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())
  const days = Math.round((endMidnight - fromMidnight) / MS_PER_DAY)

  return days < 0 ? null : days
}

/**
 * Product discriminator for ui/validate/Coupon. The server maps this to the
 * configured AdvicePrep Stripe product — the frontend must not hold a Stripe id.
 */
export const ADVICEPREP_COUPON_PRODUCT = 'adviceprep'

// Stripe's price nicknames have used both spellings ("AdvicePrepFlatFee",
// "AdvisePrep Monthly"). Dropping either silently removes the subscription from Billing.
export const ADVICEPREP_PRICE_NICKNAME_PREFIXES = ['AdvicePrep', 'AdvisePrep'] as const

export function isAdvicePrepPriceNickname(nickname: string): boolean {
  return ADVICEPREP_PRICE_NICKNAME_PREFIXES.some((prefix) => nickname.startsWith(prefix))
}
